import { observer } from "mobx-react";
import { useCallback, useState } from "react";
import useTranslation from "../../../../../../translation/hooks/translation-hook";
import useNavigationBlocker from "../../../../../../hooks/navigation-blocker-hook";
import EditorPageLayout from "../../../../../../components/layouts/editor-page/editor-page-layout";
import ListLayout from "../../../../../../components/layouts/list-layout/list-layout";
import ControlGroupWithPreview from "../../../../../../components/layouts/control-group-with-preview/control-group-with-preview";
import ShipmentStatusOrderedPreview from '../../../../../../images/previews/shipment-status-ordered.svg';
import ShipmentStatusPackedPreview from '../../../../../../images/previews/shipment-status-packed.svg';
import ShipmentStatusInTransitPreview from '../../../../../../images/previews/shipment-status-in-transit.svg';
import ShipmentStatusOutForDeliveryPreview from '../../../../../../images/previews/shipment-status-out-for-delivery.svg';
import ShipmentStatusDeliveredPreview from '../../../../../../images/previews/shipment-status-delivered.svg';
import ShipmentStatusDeliveryFailedPreview from '../../../../../../images/previews/shipment-status-delivery-failed.svg';
import { FormField } from "../../../../../../components/form-field/form-field";
import { TRACKING_PORTAL_TRANSLATION_KEYS } from "../../constants";
import TranslationInput from "../../../../../../translation/translation-input/translation-input";
import { ConfirmModal } from "../../../../../../components/modal/components/confirm-modal/confirm-modal";
import TranslationTextarea from "../../../../../../translation/translation-textarea/translation-textarea";

const TrackingStatusesEditor: React.FC = observer(() => {
    const [isShowUnsaved, setIsShowUnsaved] = useState(false);

    const {
        hasChanges: hasTranslationChanges,
        discardChanges: translationsDiscardChanges,
        save: saveTranslations
    } = useTranslation();

    const hasChanges = () => {
        return hasTranslationChanges();
    }

    const { retryNavigation, unblockNavigation } = useNavigationBlocker(
        () => hasChanges(),
        useCallback(() => setIsShowUnsaved(true), [setIsShowUnsaved])
    );

    const discardChanges = () => {
        translationsDiscardChanges();
    }

    const onSaveClicked = () => {
        const promised = [];

        if (hasTranslationChanges()) {
            promised.push(
                saveTranslations()
            );
        }

        Promise.all(promised).then(() => {
            unblockNavigation();
        })
    }

    return (
        <>
            <EditorPageLayout
                toolbar={{
                    saveButton: {
                        hasChanges: hasChanges(),
                        disabled: !hasChanges(),
                        onClick: onSaveClicked
                    }
                }}
            >

                <ListLayout>
                    <ControlGroupWithPreview hasTopBorder={false} preview={<img src={ShipmentStatusOrderedPreview} alt="" />}>
                        <ListLayout>                                                        
                            <FormField
                                label='"Ordered" Status Name'
                                instructions="It is recommended to keep status names between 1-3 words long."
                            >
                                <TranslationInput translationKey={TRACKING_PORTAL_TRANSLATION_KEYS.PENDING_SHIPMENT} />
                            </FormField>

                            <FormField label="Status Description">
                                <TranslationTextarea translationKey={TRACKING_PORTAL_TRANSLATION_KEYS.TRACKING_DESCRIPTION_PENDING_SHIPMENT} />
                            </FormField>
                        </ListLayout>
                    </ControlGroupWithPreview>

                    <ControlGroupWithPreview preview={<img src={ShipmentStatusPackedPreview} alt="" />}>
                        <ListLayout>                                                        
                            <FormField
                                label='"Packed" Status Name'
                                instructions="It is recommended to keep status names between 1-3 words long."
                            >
                                <TranslationInput translationKey={TRACKING_PORTAL_TRANSLATION_KEYS.LABEL_CREATED} />
                            </FormField>

                            <FormField label="Status Description">
                                <TranslationTextarea translationKey={TRACKING_PORTAL_TRANSLATION_KEYS.TRACKING_DESCRIPTION_LABEL_CREATED} />
                            </FormField>
                        </ListLayout>
                    </ControlGroupWithPreview>

                    <ControlGroupWithPreview preview={<img src={ShipmentStatusInTransitPreview} alt="" />}>
                        <ListLayout>                                                        
                            <FormField
                                label='"In Transit" Status Name'
                                instructions="It is recommended to keep status names between 1-3 words long."
                            >
                                <TranslationInput translationKey={TRACKING_PORTAL_TRANSLATION_KEYS.SHIPPED} />
                            </FormField>

                            <FormField label="Status Description">
                                <TranslationTextarea translationKey={TRACKING_PORTAL_TRANSLATION_KEYS.TRACKING_DESCRIPTION_SHIPPED} />
                            </FormField>
                        </ListLayout>
                    </ControlGroupWithPreview>

                    <ControlGroupWithPreview preview={<img src={ShipmentStatusOutForDeliveryPreview} alt="" />}>
                        <ListLayout>                                                        
                            <FormField
                                label='"Out for Delivery" Status Name'
                                instructions="It is recommended to keep status names between 1-3 words long."
                            >
                                <TranslationInput translationKey={TRACKING_PORTAL_TRANSLATION_KEYS.OUT_FOR_DELIVERY} />
                            </FormField>

                            <FormField label="Status Description">
                                <TranslationTextarea translationKey={TRACKING_PORTAL_TRANSLATION_KEYS.TRACKING_DESCRIPTION_OUT_FOR_DELIVERY} />
                            </FormField>
                        </ListLayout>
                    </ControlGroupWithPreview>

                    <ControlGroupWithPreview preview={<img src={ShipmentStatusDeliveredPreview} alt="" />}>
                        <ListLayout>                                                        
                            <FormField
                                label='"Delivered" Status Name'
                                instructions="It is recommended to keep status names between 1-3 words long."
                            >
                                <TranslationInput translationKey={TRACKING_PORTAL_TRANSLATION_KEYS.RECEIVED} />
                            </FormField>

                            <FormField label="Status Description">
                                <TranslationTextarea translationKey={TRACKING_PORTAL_TRANSLATION_KEYS.TRACKING_DESCRIPTION_RECEIVED} />
                            </FormField>
                        </ListLayout>
                    </ControlGroupWithPreview>

                    <ControlGroupWithPreview preview={<img src={ShipmentStatusDeliveryFailedPreview} alt="" />}>
                        <ListLayout>                                                        
                            <FormField
                                label='"Delivery Failed" Status Name'
                                instructions="It is recommended to keep status names between 1-3 words long."
                            >
                                <TranslationInput translationKey={TRACKING_PORTAL_TRANSLATION_KEYS.REJECTED} />
                            </FormField>

                            <FormField label="Status Description">
                                <TranslationTextarea translationKey={TRACKING_PORTAL_TRANSLATION_KEYS.TRACKING_DESCRIPTION_REJECTED} />
                            </FormField>
                        </ListLayout>
                    </ControlGroupWithPreview>
                </ListLayout>
            </EditorPageLayout>

            {isShowUnsaved &&
                <ConfirmModal
                    cancelButton={{
                        title: 'Cancel',
                        onClick: () => setIsShowUnsaved(false)
                    }}
                    okButton={{
                        title: 'Discard',
                        onClick: () => {
                            discardChanges();
                            setIsShowUnsaved(false);
                            retryNavigation();
                        }
                    }}
                />
            }
        </>
    );
});

export default TrackingStatusesEditor;